import React, { useState, useCallback } from 'react';
import debounce from 'lodash/debounce';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';

import ErrorMessage from 'UI/ErrorMessage/ErrorMessage';

import { ReactComponent as Logo } from 'assets/icons/logo_2@2x.svg';
import { ReactComponent as Facebook } from 'assets/icons/facebook.svg';
import { ReactComponent as Twitter } from 'assets/icons/twitter.svg';
import { ReactComponent as YouTube } from 'assets/icons/youtube.svg';
import { ReactComponent as Instagram } from 'assets/icons/instagram.svg';
import { ReactComponent as Discord } from 'assets/icons/discord.svg';
// import { ReactComponent as Twitch } from 'assets/icons/twitch.svg';

import { getNewsletterState, getFooter } from 'store/footer/selector';
import { getFooterRequest } from 'store/footer/actions';
import { getUser } from 'store/user/selector';

import { checkValidity, updateObject } from 'shared/utility';
import { SUPPORT, MAIN_URLS} from 'shared/urlList';

import { initialFormState } from './constants';

import API from 'API';

import styles from './Footer.module.scss';

const Footer = () => {
	const [formControls, setFormControls] = useState(initialFormState);
	// eslint-disable-next-line
	const [formIsValid, setFormIsValid] = useState(false);
	// eslint-disable-next-line
    const [errorMessage, setErrorMessage] = useState('');
	const [joined, setJoined] = useState(useSelector(getNewsletterState, shallowEqual));
	const [userInfo] = useState(useSelector(getUser, shallowEqual));
	const [{ data } : footerData] = useState(useSelector(getFooter, shallowEqual));
	const dispatch = useDispatch();

	const joinNewsletter = async () => {
		try {
			const payload = {
				email: formControls.email.value,
			};
			await API.post('/content/subscribe', payload);
			await dispatch(getFooterRequest());
			setJoined(true);
		} catch (error) {
			// eslint-disable-next-line
			console.warn('Newsletter', error);
			setErrorMessage(error.data.error);
		}
	};

	const formValidation = useCallback(debounce((formControls, value, identifier) => {
		if (value) {
			const val = checkValidity(
				value,
				formControls[identifier].validation
			);

			const isValid = val.isValid;
			const errorMessage = val.message;

			const updatedFormElement = updateObject(formControls[identifier], {
				valid: isValid,
				touched: true,
				errorMessage,
				value
			});

			const controls = updateObject(formControls, {
				[identifier]: updatedFormElement,
			});

			let formIsValid = true;

			// eslint-disable-next-line
			for (let identifier in controls) {
				formIsValid = controls[identifier].valid && formIsValid;
			}

			setFormControls(controls);
			setFormIsValid(formIsValid);
		}
	}, 1000), []);

    const inputChangedHandler = (event, identifier) => {
		setErrorMessage('');
		setFormIsValid(false);

		const updatedFormElement = updateObject(formControls[identifier], {
			value: event.target.value.trim(),
			errorMessage: '',
			valid: true,
        });

        const controls = updateObject(formControls, {
			[identifier]: updatedFormElement,
        });

        setFormControls(controls);
		formValidation(formControls, event.target.value.trim(), identifier);
    };

	const { email } = formControls;

    return (
        <footer className={styles.footer_wrapper}>
			<div className={styles.footer}>
				<Logo className={styles.logo} />
				<section>
					<div className={styles.wrapper}>
						<div className={styles.list}>
							<ul>
								<Link to='/upgrade-account'>
									<li>Become a pro member</li>
								</Link>
								<Link to={SUPPORT.SUPPORT}>
									<li>Frequently asked questions</li>
								</Link>
								<a href='https://discord.gg/MBD4RamdcX' target='_bank'>
									<li>Community</li>
								</a>
							</ul>
						</div>
						<div className={styles.list}>
							<ul>
								<Link to={MAIN_URLS.CHESS_RULES}>
									<li>Chess rules</li>
								</Link>
								{userInfo && <Link to={SUPPORT.REQUEST}>
									<li>Contact us</li>
								</Link>}
							</ul>
						</div>
					</div>
					<div className={styles.subscription}>
						<div className={styles.sub}>
							<h2>
								Want to stay updated with the latest content and events from K-chess?
							</h2>
							<div className={[styles.email, !email.errorMessage && !errorMessage ? null : styles.invalid, joined ? styles.email_joined : null].join(' ')}>
								{!joined && <input
									{...email.elementConfig}
									value={email.value}
									onChange={(e) => inputChangedHandler(e, 'email')}
									noValidate
									className={!email.errorMessage && !errorMessage ? null : styles.invalid}
								/>}
								<div role="button" className={[styles.button, joined ? styles.joined : null].join(' ')} onClick={!email.errorMessage && !errorMessage && email.value ? joinNewsletter : null}>
									{joined ?
									<svg className={styles.animated_check} viewBox="0 0 24 24">
										<path d="M4.1 12.7L9 17.6 20.3 6.3"/>
									</svg> :
									'Subscribe'}
								</div>
							</div>
							<ErrorMessage show={email.errorMessage}>
								<FormattedMessage
									id={email.errorMessage}
									values={{
										maxLength: email.validation.maxLength,
										minLength: email.validation.minLength,
									}}
								/>
							</ErrorMessage>
							<ErrorMessage show={errorMessage}>
								<FormattedMessage
									id={errorMessage && `error.${errorMessage}`}
								/>
							</ErrorMessage>
							{joined && <div className={styles.thanks}>Thank you, we’ll keep you posted!</div>}
						</div>
						{/*
						<div className={styles.social} style={{ marginTop: email.errorMessage || errorMessage ? '12px' : null }}>
							<a
								href="https://www.facebook.com/Kasparovchess-104990161721049"
								target="_blank"
								rel="noopener noreferrer"
								className={styles.facebook}
							>
								<Facebook />
							</a>
							<a
								href="https://twitter.com/Kasparovchess"
								target="_blank"
								rel="noopener noreferrer"
								className={styles.twitter}
							>
								<Twitter />
							</a>
							<a
								href="https://www.youtube.com/channel/UCfGw2EKElc4eRh9r4iwy8yQ"
								target="_blank"
								rel="noopener noreferrer"
								className={styles.youtube}
							>
								<YouTube />
							</a>
							<a
								href="https://www.instagram.com/kasparovchess_"
								target="_blank"
								rel="noopener noreferrer"
								className={styles.instagram}
							>
								<Instagram />
							</a>
							<a
								href="https://discord.gg/MBD4RamdcX"
								target="_blank"
								rel="noopener noreferrer"
								className={styles.discord}
							>
								<Discord />
							</a>
						</div>
						*/}
					</div>
				</section>
				<div className={styles.copyright}>
					{`© ${new Date().getFullYear()} Keysquare. All Rights Reserved.`}

					<Link to='/terms-and-conditions'>
						Terms and conditions
					</Link>
					/
					<Link to='/privacy-policy'>
						Privacy policy
					</Link>
				</div>
			</div>
        </footer>
    );
};

export default Footer;
